/** PAGINA «VÂNZĂRI ÎN ZONĂ» a Planificării: ofertele de vânzare din jurul
 *  terenului, culese din anunțurile publice (`market-price-collector/`).
 *
 *  Ce face pagina: cere colectarea (butonul), întreabă serverul la câteva
 *  secunde cât timp e în lucru — ofertele găsite apar pe măsură ce vin — și
 *  arată ce s-a găsit: pe camere, cu distanța până la teren, anul blocului,
 *  finisajele și fațada estimate din textul anunțului și ponderea fiecărei
 *  oferte în profilul zonei. La clic, tot ce s-a citit despre anunț și
 *  legătura spre el.
 *
 *  Deasupra listei, CONFIGURAȚIA RECOMANDATĂ a zonei (`CardPiata`, același card
 *  ca pe brief, aici numai de citit) și, sub ea, cifrele din care iese — media
 *  ponderată, împărțirile —, socotite de colector pe aceleași oferte
 *  (`market-price-collector/profil.py`). */
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../api";
import { dataOra, numar, t, tn, useT } from "../../i18n";
import {
  type Anunt, type Colectare, type GrupaCamere, type Oferta, type Piata, type PlajaPret, type Profil,
  type Sortare, SORTARI, colecteaza, distributie, filtreaza, getAnunt, getPiata, getProfil, inLucru,
  numeSursa, pesurse, proaspatPanaLa, sorteaza,
} from "./piata";
import CardPiata from "./CardPiata";
import { Lamurire } from "./Termeni";
import "./piata.css";

const SONDAJ_MS = 5000;
// lista întreagă se cere din nou o dată la atâtea tice (30 s); între ele, numai starea
const TICE_PE_LISTA = 6;
const PE_PAGINA = 60;

const bani = (v: number | null, moneda: string | null) =>
  v == null ? "—" : `${numar(v, { maximumFractionDigits: 0 })} ${!moneda || moneda === "EUR" ? "€" : moneda}`;
const km = (v: number) => numar(v, { maximumFractionDigits: v < 10 ? 1 : 0 });

function etaj(o: Pick<Oferta, "etaj" | "etaje_cladire">): string {
  const e = o.etaj == null ? "—" : o.etaj === 0 ? t("pz.etaj.parter") : o.etaj < 0 ? t("pz.etaj.demisol")
    : o.etaj > 10 ? "> 10" : String(o.etaj);
  return o.etaje_cladire ? `${e}/${o.etaje_cladire}` : e;
}

const CLASA_FIN = { low: "ip-pill-amb", standard: "ip-pill-mut", premium: "ip-pill-acc", luxury: "ip-pill-met" } as const;

// Codurile sursei pentru structură și încălzire, cum le citește omul; un cod
// necunoscut rămâne cum vine, ca să nu se piardă.
const MATERIALE = ["concrete", "brick", "reinforced_concrete", "concrete_plate", "cellular_concrete",
                   "breezeblock", "wood", "other"] as const;
const INCALZIRI = ["gas", "urban", "boiler_room", "other"] as const;
const material = (v: string | null) => (v == null ? "—"
  : (MATERIALE as readonly string[]).includes(v) ? t(`pz.material.${v as (typeof MATERIALE)[number]}`) : v);
const incalzire = (v: string | null) => (v == null ? "—"
  : (INCALZIRI as readonly string[]).includes(v) ? t(`pz.incalzire.${v as (typeof INCALZIRI)[number]}`) : v);

function Finisaje({ o }: { o: Pick<Oferta, "clasificare"> }) {
  const f = o.clasificare?.finisaje;
  if (!f?.clasa) return <span className="sg-muted">—</span>;
  return (
    <span className={`ip-pill ${CLASA_FIN[f.clasa]}`}
          title={t("pz.din_text", { dovezi: f.dovezi.join("», «") })}>{t(`pz.fin.${f.clasa}`)}</span>
  );
}

function Fatada({ o }: { o: Pick<Oferta, "clasificare"> }) {
  const f = o.clasificare?.fatada;
  if (!f?.tip) return <span className="sg-muted">—</span>;
  return (
    <span className={`ip-pill ${f.tip === "umeda" ? "ip-pill-mut" : "ip-pill-acc"}`}
          title={`${t(`pz.fat.${f.tip}_t`)} — ${t("pz.din_text", { dovezi: f.dovezi.join("», «") })}`}>
      {t(`pz.fat.${f.tip}`)}</span>
  );
}

function An({ o, vechime }: { o: Pick<Oferta, "an_constructie" | "vechi">; vechime: number }) {
  if (o.an_constructie == null) return <span className="sg-muted">—</span>;
  return (
    <>
      {o.an_constructie}
      {o.vechi && (
        <span className="ip-pill ip-pill-amb pz-vechi"
              title={t("pz.vechi_t", { an: o.an_constructie, ani: tn("pz.n_ani#n", vechime) })}>{t("pz.vechi")}</span>
      )}
    </>
  );
}

/** «+ TVA» lângă prețul unui anunț care spune că prețul lui e fără TVA: profilul
 *  îl ia ca preț net, pe celelalte le împarte la 1 + cota. */
function FaraTva({ o }: { o: Pick<Oferta, "clasificare"> }) {
  const v = o.clasificare?.tva;
  if (v?.pret !== "fara") return null;
  return (
    <span className="ip-pill ip-pill-mut pz-tva"
          title={t("pz.tva_fara_t", { dovezi: v.dovezi.join("», «") })}>{t("pz.tva_fara")}</span>
  );
}

function Suspect({ o }: { o: Pick<Oferta, "clasificare"> }) {
  const motive = o.clasificare?.suspect ?? [];
  if (!motive.length) return null;
  return (
    <span className="ip-pill ip-pill-bad pz-suspect"
          title={motive.map((m) => t(`pz.suspect.${m}`)).join(" ")}>{t("pz.suspect")}</span>
  );
}

/** Rândul de stare de sus: când s-a căutat, cât de larg, ce se întâmplă acum. */
function Stare({ c, af, piata, mesaj }: { c: Colectare | null; af: Colectare | null; piata: Piata; mesaj: string | null }) {
  useT();
  const s = piata.setari;
  const surse = af ? Object.entries(af.surse) : [];
  return (
    <div className="pz-stare">
      {af?.terminat_la && af.id !== c?.id && inLucru(c) && <div className="sg-muted">{t("pz.se_arata_vechi")}</div>}
      {surse.map(([cod, sr]) => (
        <div key={cod} className="pz-linie">
          <b>{numeSursa(cod)}</b>
          {sr.treapta && (
            <span>{sr.treapta.an_minim
              ? t("pz.zona", { raza: km(sr.treapta.raza_km), an: sr.treapta.an_minim })
              : t("pz.zona_orice", { raza: km(sr.treapta.raza_km) })}</span>
          )}
          {sr.largita && (sr.trepte?.length ?? 0) > 1 && (
            <Lamurire text={`${t("pz.trepte", { lista: sr.trepte!.map((tr) => tr.ani
              ? t("pz.treapta_item", { raza: km(tr.raza_km), ani: tn("pz.n_ani#n", tr.ani), oferte: tn("pz.n_oferte#n", tr.numar) })
              : t("pz.treapta_item_orice", { raza: km(tr.raza_km), oferte: tn("pz.n_oferte#n", tr.numar) })).join(" → ") })} ${t("pz.regula", {
              esantion: tn("pz.n_oferte#n", s.tinta_oferte), tkm: km(s.tinta_km),
              ani: tn("pz.n_ani#n", s.tinta_ani), min: tn("pz.n_oferte#n", s.min_oferte) })}`} />
          )}
          {af?.terminat_la && <span className="sg-muted">{t("pz.cautat_la", { data: dataOra(af.terminat_la) })}</span>}
          {sr.stare === "interzis" && <span className="ip-pill ip-pill-bad">{t("pz.sursa.interzis")}</span>}
          {sr.stare === "neconfigurat" && <span className="ip-pill ip-pill-mut">{t("pz.sursa.neconfigurat")}</span>}
          {sr.stare === "eroare" && <span className="ip-pill ip-pill-bad">{t("pz.stare.eroare")}</span>}
          {sr.motiv && sr.stare !== "ok" && <span className="sg-muted">{sr.motiv}</span>}
        </div>
      ))}
      {inLucru(c) && (
        <div className="pz-linie">
          <span className="ip-pill ip-pill-amb">{t(c!.stare === "ruleaza" ? "pz.stare.ruleaza" : "pz.stare.in_asteptare")}</span>
          {Object.entries(c!.progres).map(([cod, p]) => (
            <span key={cod} className="sg-muted">
              {numeSursa(cod)}: {p.etapa === "detalii" ? t("pz.progres.detalii", { facut: numar(p.facut ?? 0), total: numar(p.total ?? 0) })
                : p.etapa === "cautare" ? t("pz.progres.cautare", { n: numar(p.anunturi ?? 0) })
                  : t("pz.progres.locatii")}
            </span>
          ))}
        </div>
      )}
      {c?.stare === "eroare" && (
        <div className="pz-linie"><span className="ip-pill ip-pill-bad">{t("pz.stare.eroare")}</span>
          <span className="sg-muted">{c.eroare}</span></div>
      )}
      {mesaj && <div className="pz-avert">{mesaj}</div>}
    </div>
  );
}

const euro = (v: number | null | undefined) => (v == null ? "—" : `${numar(v, { maximumFractionDigits: 0 })} €`);
const pct = (v: number) => `${numar(v * 100, { maximumFractionDigits: 0 })} %`;
const semn = (d: number) => `${d > 0 ? "+" : "−"}${numar(Math.abs(d) * 100, { maximumFractionDigits: 0 })} %`;
const grupaText = (g: GrupaCamere) => (g === "?" ? t("pz.f.camere.necunoscut") : t(`pz.f.camere.${g}`));

/** «2.323 · 80 % între 1.472 și 3.934»: cât de împrăștiate sunt prețurile. */
const plaja = (p: PlajaPret) =>
  t("pz.p.plaja", { mediana: euro(p.mediana), p10: euro(p.p10), p90: euro(p.p90) });

/** CIFRELE ZONEI, sub recomandare: un rând cu media, iar la cerere tot profilul
 *  — pe camere, finisaje, fațade, piață, distanță — și pe ce stă fiecare parte a
 *  recomandării. Cifrele vin gata socotite de la colector; aici se spun. */
function CifreleZonei({ p }: { p: Profil }) {
  useT();
  const [deschis, setDeschis] = useState(false);
  const excluse = (["suspect", "ansamblu", "fara_pret", "alta_moneda"] as const)
    .filter((k) => p.excluse[k] > 0).map((k) => tn(`pz.p.ex.${k}#n`, p.excluse[k]));
  const r = p.recomandare;
  const factor = r ? numar(1 + r.pret.cota_tva, { maximumFractionDigits: 2 }) : "";
  return (
    <section className="pz-profil ip-card" aria-labelledby="pz-profil-h">
      <div className="pz-p-cap">
        <h3 id="pz-profil-h">{t("cp.cifrele")}</h3>
        <Lamurire text={<>{t("pz.p.pe_ce", { oferte: tn("pz.n_oferte#n", p.esantion.n) })}
          {p.aproximative.n > 0 && p.aproximative.pret_mp && <> {t("pz.p.aprox", { oferte: tn("pz.n_oferte#n", p.aproximative.n),
            ponderat: `${euro(p.aproximative.pret_mp.ponderat)}/m²`, mediana: euro(p.aproximative.pret_mp.mediana) })}</>}</>} />
        {p.pret_mp && (
          <span className="pz-p-pret">
            <b>{euro(p.pret_mp.ponderat)}/m²</b>
            <span>{t("pz.p.ponderat")}</span>
            <span className="sg-muted">{plaja(p.pret_mp)}</span>
          </span>
        )}
        <span className="pz-sp" />
        <button type="button" className="btn btng" aria-expanded={deschis} onClick={() => setDeschis(!deschis)}>
          {t(deschis ? "cp.cifrele_ascunde" : "cp.cifrele_arata")}</button>
      </div>
      {deschis && (
        <>
          {excluse.length > 0 && <div className="sg-muted">{t("pz.p.excluse", { lista: excluse.join(", ") })}</div>}

          {p.camere.length > 0 && (
            <div className="pz-p-derula"><table className="sg-table pz-p-tab">
              <thead>
                <tr><th>{t("pz.p.camere")}</th>{p.camere.map((c) => <th key={c.grupa} className="bc-n">{grupaText(c.grupa)}</th>)}</tr>
              </thead>
              <tbody>
                <tr><td>{t("pz.p.parte")}</td>{p.camere.map((c) => <td key={c.grupa} className="sg-mono bc-n">{pct(c.pct)}</td>)}</tr>
                <tr><td>{t("pz.p.pret_mp")}</td>{p.camere.map((c) => <td key={c.grupa} className="sg-mono bc-n">{euro(c.pret_mp)}</td>)}</tr>
                <tr><td>{t("pz.p.suprafata")}</td>{p.camere.map((c) => (
                  <td key={c.grupa} className="sg-mono bc-n">{c.suprafata == null ? "—" : `${numar(c.suprafata, { maximumFractionDigits: 0 })} m²`}</td>))}</tr>
                <tr><td>{t("pz.p.oferte")}</td>{p.camere.map((c) => <td key={c.grupa} className="sg-mono bc-n">{numar(c.n)}</td>)}</tr>
              </tbody>
            </table></div>
          )}

          <div className="pz-p-doua">
            <Impartire titlu={t("pz.p.finisaje")} necunoscute={p.finisaje.necunoscute}
                       randuri={p.finisaje.impartire.map((c) => ({ k: c.clasa, eticheta: t(`pz.fin.${c.clasa}`), ...c }))} />
            <Impartire titlu={t("pz.p.fatade")} necunoscute={p.fatade.necunoscute}
                       randuri={p.fatade.impartire.map((c) => ({ k: c.tip, eticheta: t(`pz.fat.${c.tip}`), ...c }))} />
          </div>

          {p.piete.length > 0 && (
            <div className="pz-p-linii">
              <div className="pz-linie">
                <span className="pz-et">{t("pz.p.piata")}</span>
                {p.piete.map((x) => (
                  <span key={x.piata}>{t(`pz.piata.${x.piata}`)}: <b>{pct(x.pct)}</b> · {euro(x.pret_mp)}/m²
                    <span className="sg-muted"> ({t("pz.p.mediana", { v: euro(x.mediana) })}, {tn("pz.n_oferte#n", x.n)})</span></span>
                ))}
              </div>
              {p.tva.fara + p.tva.cu > 0 && (
                <div className="sg-muted">{tn("pz.p.tva#n", p.tva.fara)}; {t("pz.p.tva_rest", { cu: numar(p.tva.cu), nespus: numar(p.tva.nespus) })}</div>
              )}
              {p.inele.length > 1 && (
                <div className="pz-linie">
                  <span className="pz-et">{t("pz.p.inele")}</span>
                  {p.inele.map((i) => (
                    <span key={i.de_la}>{i.de_la === 0 ? t("pz.p.inel_sub", { km: numar(i.pana_la) })
                      : t("pz.p.inel", { de_la: numar(i.de_la), pana_la: numar(i.pana_la) })}: {euro(i.pret_mp)}/m²
                      <span className="sg-muted"> ({numar(i.n)})</span></span>
                  ))}
                </div>
              )}
            </div>
          )}

          {r && (
            <div className="pz-p-linii pz-p-pe">
              <span className="pz-et">{t("cp.pe_ce_sta")}</span>
              <span>{t("pz.p.rec.mix_pe", { oferte: tn("pz.n_oferte#n", r.mix_n) })}</span>
              {r.finisaje && (
                <span>{t("pz.p.rec.finisaje_pe", { pct: pct(r.finisaje.pct), estimate: tn("pz.n_oferte#n", r.finisaje.n_estimate),
                                                   n: tn("pz.n_oferte#n", r.finisaje.n) })}
                  {r.finisaje.fata_de.length > 0 && <> {t("pz.p.rec.fata_de", { lista: r.finisaje.fata_de
                    .map((d) => `${t(`pz.fin.${d.clasa}`)} ${semn(d.dif)}`).join(", ") })}</>}</span>
              )}
              <span>{r.finisaje
                ? t("pz.p.rec.pret_pe_finisaj", { oferte: tn("pz.n_oferte#n", r.pret.n), clasa: t(`pz.fin.${r.finisaje.clasa}`),
                    cerut: euro(r.pret.cerut_mp), fara: numar(r.pret.fara_tva), factor })
                : t("pz.p.rec.pret_pe_toate", { oferte: tn("pz.n_oferte#n", r.pret.n), cerut: euro(r.pret.cerut_mp),
                    fara: numar(r.pret.fara_tva), factor })} {t("pz.p.rec.cerute")}</span>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** O împărțire din text (finisaje, fațade): parte, preț, câte oferte; cele fără
 *  semne se spun dedesubt, nu ca rând — tăcerea anunțului nu e o clasă. */
function Impartire({ titlu, randuri, necunoscute }: {
  titlu: string; necunoscute: number;
  randuri: { k: string; eticheta: string; n: number; pct: number; pret_mp: number | null }[];
}) {
  return (
    <div className="pz-p-imp">
      <span className="pz-et">{titlu}</span>
      {randuri.length > 0 ? (
        <table className="sg-table pz-p-tab">
          <tbody>
            {randuri.map((x) => (
              <tr key={x.k}>
                <td>{x.eticheta}</td>
                <td className="sg-mono bc-n">{pct(x.pct)}</td>
                <td className="sg-mono bc-n">{euro(x.pret_mp)}/m²</td>
                <td className="sg-mono bc-n sg-muted">{numar(x.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {necunoscute > 0 && <div className="sg-muted">{tn("pz.p.fara_semne#n", necunoscute)}</div>}
    </div>
  );
}

function Detaliu({ projectId, id, oferta, vechime, laInchide }: {
  projectId: string; id: number; oferta: Oferta | null; vechime: number; laInchide: () => void;
}) {
  useT();
  const [a, setA] = useState<Anunt | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [poza, setPoza] = useState(0);
  const [toata, setToata] = useState(false);
  useEffect(() => {
    let viu = true;
    setA(null); setEroare(null); setPoza(0); setToata(false);
    getAnunt(projectId, id)
      .then((r) => { if (viu) setA(r); })
      .catch((e) => { if (viu) setEroare(e instanceof ApiError ? e.message : String(e)); });
    return () => { viu = false; };
  }, [projectId, id]);

  if (eroare) return <aside className="pz-detaliu ip-card"><div className="sg-error">{eroare}</div></aside>;
  if (!a) return <aside className="pz-detaliu ip-card"><div className="sg-muted">{t("comun.se_incarca")}</div></aside>;
  const imagini = a.imagini.filter((i) => i.mare || i.mic);
  const ansamblu = (a.caracteristici.fel ?? []).includes("ansamblu");
  const fapte: [string, ReactNode][] = [
    [t("pz.d.pret"), <span key="pret">{bani(a.pret, a.moneda)}<FaraTva o={a} /></span>],
    [t("pz.d.pret_mp"), bani(a.pret_mp, a.moneda)],
    [t("pz.d.suprafata"), a.suprafata_mp == null ? "—" : `${numar(a.suprafata_mp)} m²`],
    [t("pz.d.camere"), a.camere ?? "—"],
    [t("pz.d.etaj"), etaj(a)],
    [t("pz.d.an"), <An key="an" o={{ an_constructie: a.an_constructie, vechi: oferta?.vechi ?? false }} vechime={vechime} />],
    [t("pz.d.piata"), a.piata ? t(`pz.piata.${a.piata}`) : "—"],
    [t("pz.d.stare"), a.stare_constructie && ["gata", "de_finisat", "de_renovat", "in_constructie"].includes(a.stare_constructie)
      ? t(`pz.stare_c.${a.stare_constructie as "gata" | "de_finisat" | "de_renovat" | "in_constructie"}`)
      : a.stare_constructie ?? "—"],
    [t("pz.d.material"), material(a.material)],
    [t("pz.d.incalzire"), incalzire(a.incalzire)],
    [t("pz.d.cartier"), a.cartier ?? "—"],
    [t("pz.d.adresa"), a.adresa ?? "—"],
    [t("pz.d.agentie"), a.agentie ?? "—"],
  ];
  if (oferta) {
    fapte.push([t("pz.d.distanta"), `${oferta.distanta_aprox ? "≈ " : ""}${km(oferta.distanta_km)} km`]);
    fapte.push([t("pz.d.pondere"), numar(oferta.pondere, { maximumFractionDigits: 2 })]);
  }
  fapte.push([t("pz.d.publicat"), a.publicat_la ? dataOra(a.publicat_la) : "—"]);
  fapte.push([t("pz.d.citit"), a.detalii_la ? dataOra(a.detalii_la) : t("pz.d.necitit")]);
  const fin = a.clasificare?.finisaje, fat = a.clasificare?.fatada;
  return (
    <aside className="pz-detaliu ip-card">
      <div className="pz-d-cap">
        <b className="pz-d-titlu">{a.titlu}</b>
        {ansamblu && <span className="ip-pill ip-pill-mut">{t("pz.ansamblu")}</span>}
        <button type="button" className="btn btng" onClick={laInchide} aria-label={t("pz.d.inchide")}>✕</button>
      </div>
      <a className="btn" href={a.url} target="_blank" rel="noreferrer noopener">
        {t("pz.d.deschide", { sursa: numeSursa(a.sursa) })}</a>
      {imagini.length > 0 && (
        <div className="pz-galerie">
          <a href={imagini[poza].mare ?? imagini[poza].mic ?? undefined} target="_blank" rel="noreferrer noopener">
            <img className="pz-poza" src={imagini[poza].mare ?? imagini[poza].mic ?? undefined}
                 alt={a.titlu ?? ""} referrerPolicy="no-referrer" />
          </a>
          {imagini.length > 1 && (
            <div className="pz-miniaturi">
              {imagini.map((im, i) => (
                <button key={i} type="button" className={`pz-mini ${i === poza ? "ales" : ""}`} onClick={() => setPoza(i)}>
                  <img src={im.mic ?? im.mare ?? undefined} alt="" loading="lazy" referrerPolicy="no-referrer" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <dl className="pz-fapte">
        {fapte.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
      {(oferta?.dubluri?.length ?? 0) > 0 && (
        <div className="pz-linie">
          <span className="pz-et">{t("pz.d.dubluri")}</span>
          {oferta!.dubluri!.map((d) => (
            <a key={`${d.sursa}-${d.id}`} href={d.url} target="_blank" rel="noreferrer noopener">
              {numeSursa(d.sursa)} ↗</a>
          ))}
        </div>
      )}
      {(a.clasificare?.suspect?.length ?? 0) > 0 && (
        <div className="pz-avert">
          <Suspect o={a} /> {a.clasificare!.suspect!.map((m) => t(`pz.suspect.${m}`)).join(" ")}
        </div>
      )}
      <div className="pz-clasa">
        <div><span className="pz-et">{t("pz.d.finisaje")}</span> <Finisaje o={a} />
          {fin?.dovezi.length ? <div className="sg-muted">{t("pz.din_text", { dovezi: fin.dovezi.join("», «") })}</div>
            : <div className="sg-muted">{t("pz.d.fara_semne")}</div>}</div>
        <div><span className="pz-et">{t("pz.d.fatada")}</span> <Fatada o={a} />
          {fat?.dovezi.length ? <div className="sg-muted">{t("pz.din_text", { dovezi: fat.dovezi.join("», «") })}</div>
            : <div className="sg-muted">{t("pz.d.fara_semne")}</div>}</div>
      </div>
      {Object.keys(a.caracteristici).length > 0 && (
        <details className="pz-car">
          <summary>{t("pz.d.caracteristici")}</summary>
          <dl>
            {Object.entries(a.caracteristici).map(([k, v]) => (
              <div key={k}><dt>{k.replace(/_/g, " ")}</dt><dd>{v.map((x) => x.replace(/_/g, " ")).join(", ")}</dd></div>
            ))}
          </dl>
        </details>
      )}
      {a.descriere && (
        <div className="pz-descriere">
          <span className="pz-et">{t("pz.d.descriere")}</span>
          <p className={toata ? "" : "taiata"}>{a.descriere}</p>
          {a.descriere.length > 400 && (
            <button type="button" className="btn btng" onClick={() => setToata(!toata)}>
              {t(toata ? "pz.d.mai_putin" : "pz.d.tot_textul")}</button>
          )}
        </div>
      )}
    </aside>
  );
}

export default function PanouPiata({ projectId, potScrie }: { projectId: string; potScrie: boolean }) {
  useT();
  const [stare, setStare] = useState<Piata | null>(null);
  // profilul se cere odată cu lista și se arată numai dacă a venit: fără el,
  // lista rămâne cum era
  const [profil, setProfil] = useState<Profil | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [mesaj, setMesaj] = useState<string | null>(null);
  const [lucreaza, setLucreaza] = useState(false);
  const [grupa, setGrupa] = useState<GrupaCamere | null>(null);
  const [sursa, setSursa] = useState<string | null>(null);
  const [siVechi, setSiVechi] = useState(true);
  const [sortare, setSortare] = useState<Sortare>("distanta");
  const [limita, setLimita] = useState(PE_PAGINA);
  const [ales, setAles] = useState<number | null>(null);

  const incarca = useCallback(async () => {
    try {
      const [st, pr] = await Promise.all([getPiata(projectId), getProfil(projectId).catch(() => null)]);
      setStare(st);
      setProfil(pr?.esantion ? pr : null);
      setEroare(null);
    } catch (e) {
      setEroare(e instanceof ApiError && e.status === 503 ? t("pz.colector_mut") : String((e as Error).message ?? e));
    }
  }, [projectId]);

  useEffect(() => { void incarca(); }, [incarca]);

  // cât timp se caută, pagina întreabă din nou: starea la fiecare tic, lista
  // întreagă mai rar (ofertele apar pe măsură ce vin) și o dată la capăt
  const inCurs = inLucru(stare?.colectare);
  useEffect(() => {
    if (!inCurs) return;
    let tic = 0;
    const id = window.setInterval(() => {
      tic += 1;
      if (tic % TICE_PE_LISTA === 0) { void incarca(); return; }
      getPiata(projectId, false).then((st) => {
        if (!inLucru(st.colectare)) void incarca();
        else setStare((v) => (v ? { ...v, colectare: st.colectare } : v));
      }).catch(() => {});
    }, SONDAJ_MS);
    return () => window.clearInterval(id);
  }, [inCurs, incarca, projectId]);

  const cauta = async (forteaza: boolean) => {
    setLucreaza(true);
    setMesaj(null);
    try {
      const r = await colecteaza(projectId, forteaza);
      if (r.fel === "din_cache" && stare) {
        const pana = proaspatPanaLa(r.colectare, stare.setari.cache_zile);
        setMesaj(t("pz.din_cache", { data: dataOra(r.colectare.terminat_la), zile: tn("pz.n_zile#n", stare.setari.cache_zile),
                                     pana: pana ? dataOra(pana.toISOString()) : "—" }));
      }
      await incarca();
    } catch (e) {
      setMesaj(e instanceof ApiError && e.code === "fara_locatie" ? t("pz.fara_locatie")
        : e instanceof ApiError && e.status === 503 ? t("pz.colector_mut") : String((e as Error).message ?? e));
    } finally {
      setLucreaza(false);
    }
  };

  const oferte = stare?.oferte ?? [];
  const deVazut = useMemo(() => filtreaza(oferte, { grupa: null, siVechi, sursa }), [oferte, siVechi, sursa]);
  const grupe = useMemo(() => distributie(deVazut), [deVazut]);
  const surse = useMemo(() => pesurse(filtreaza(oferte, { grupa: null, siVechi })), [oferte, siVechi]);
  const lista = useMemo(() => sorteaza(filtreaza(oferte, { grupa, siVechi, sursa }), sortare),
                        [oferte, grupa, siVechi, sursa, sortare]);
  const vechi = oferte.filter((o) => o.vechi).length;
  const oferta = ales == null ? null : oferte.find((o) => o.id === ales) ?? null;

  if (eroare && !stare) return <div className="sg-error">{eroare}</div>;
  if (!stare) return <div className="sg-muted">{t("comun.se_incarca")}</div>;
  const s = stare.setari;
  const nimic = !stare.afisata && !inCurs;

  return (
    <div className="pz">
      <div className="pz-antet ip-card">
        {nimic
          ? <div className="pz-stare"><div>{t(potScrie ? "pz.niciuna" : "pz.niciuna_cititor")}</div>
              {mesaj && <div className="pz-avert">{mesaj}</div>}</div>
          : <Stare c={stare.colectare} af={stare.afisata} piata={stare} mesaj={mesaj} />}
        {(oferte.length > 0 || stare.afisata) && (
          <Lamurire text={t("pz.nota", { km: km(s.pondere_km), ani: tn("pz.n_ani#n", s.pondere_ani) })} />
        )}
        {potScrie && (
          <div className="pz-actiuni">
            <button type="button" className="btn btnp" disabled={lucreaza || inCurs} onClick={() => void cauta(false)}>
              {t(nimic ? "pz.cauta_prima" : "pz.reimprospateaza")}</button>
            {!nimic && !inCurs && (
              <button type="button" className="btn btng" disabled={lucreaza} onClick={() => void cauta(true)}
                      title={t("pz.cauta_acum_t", { zile: tn("pz.n_zile#n", s.cache_zile) })}>{t("pz.cauta_acum")}</button>
            )}
          </div>
        )}
      </div>

      {/* Varianta A (Stefan, 31.10.2026): recomandarea întâi, de citit, fără
          butoane; cifrele din care iese, sub ea, la cerere. */}
      {profil && oferte.length > 0 && (
        <>
          <CardPiata profil={profil} colectare={stare.colectare} />
          <CifreleZonei p={profil} />
        </>
      )}

      {oferte.length > 0 && (
        <div className="pz-filtre">
          <div className="pz-cipuri" role="group" aria-label={t("pz.f.camere")}>
            <button type="button" className={`pz-cip ${grupa == null ? "ales" : ""}`} onClick={() => { setGrupa(null); setLimita(PE_PAGINA); }}>
              {t("pz.f.toate")} <span className="n">{numar(deVazut.length)}</span></button>
            {grupe.map((g) => (
              <button key={g.grupa} type="button" className={`pz-cip ${grupa === g.grupa ? "ales" : ""}`}
                      onClick={() => { setGrupa(grupa === g.grupa ? null : g.grupa); setLimita(PE_PAGINA); }}>
                {t(`pz.f.camere.${g.grupa === "?" ? "necunoscut" : g.grupa}`)}{" "}
                <span className="n">{numar(g.n)} · {numar(g.pct * 100, { maximumFractionDigits: 0 })} %</span>
              </button>
            ))}
          </div>
          {surse.length > 1 && (
            <div className="pz-cipuri" role="group" aria-label={t("pz.f.surse")}>
              <button type="button" className={`pz-cip ${sursa == null ? "ales" : ""}`}
                      onClick={() => { setSursa(null); setLimita(PE_PAGINA); }}>{t("pz.f.toate_sursele")}</button>
              {surse.map((s) => (
                <button key={s.sursa} type="button" className={`pz-cip ${sursa === s.sursa ? "ales" : ""}`}
                        onClick={() => { setSursa(sursa === s.sursa ? null : s.sursa); setLimita(PE_PAGINA); }}>
                  {numeSursa(s.sursa)} <span className="n">{numar(s.n)}</span></button>
              ))}
            </div>
          )}
          <span className="pz-sp" />
          {vechi > 0 && (
            <label className="pz-bifa">
              <input type="checkbox" checked={siVechi} onChange={(e) => setSiVechi(e.target.checked)} />
              {t("pz.f.vechi", { ani: tn("pz.n_ani#n", s.vechime_ani), n: numar(vechi) })}
            </label>
          )}
          <label className="pz-bifa">
            {t("pz.f.sortare")}
            <select value={sortare} onChange={(e) => setSortare(e.target.value as Sortare)}>
              {SORTARI.map((k) => <option key={k} value={k}>{t(`pz.sort.${k}`)}</option>)}
            </select>
          </label>
        </div>
      )}

      {oferte.length > 0 && (
        <div className={`pz-corp ${ales != null ? "cu-detaliu" : ""}`}>
          <div className="pz-lista bc-scroll">
            <table className="sg-table pz-tab">
              <thead>
                <tr>
                  <th />
                  <th>{t("pz.th.anunt")}</th>
                  <th className="bc-n">{t("pz.th.pret_mp")}</th>
                  <th className="bc-n">{t("pz.th.pret")}</th>
                  <th className="bc-n">m²</th>
                  <th className="bc-n">{t("pz.th.camere")}</th>
                  <th className="bc-n">{t("pz.th.etaj")}</th>
                  <th>{t("pz.th.an")}</th>
                  <th>{t("pz.th.finisaje")}</th>
                  <th>{t("pz.th.fatada")}</th>
                  <th className="bc-n">{t("pz.th.distanta")}</th>
                  <th className="bc-n" title={t("pz.pondere_t", { km: km(s.pondere_km), ani: tn("pz.n_ani#n", s.pondere_ani) })}>{t("pz.th.pondere")}</th>
                </tr>
              </thead>
              <tbody>
                {lista.slice(0, limita).map((o) => (
                  <tr key={o.id} className={`pz-rand ${ales === o.id ? "ales" : ""}`} onClick={() => setAles(ales === o.id ? null : o.id)}>
                    <td className="pz-foto">
                      {o.imagine ? <img src={o.imagine} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <span />}
                    </td>
                    <td className="pz-titlu">
                      <div className="t"><Suspect o={o} />{o.titlu ?? o.id_extern}</div>
                      <div className="sg-muted">
                        {[o.cartier, o.agentie, numeSursa(o.sursa)].filter(Boolean).join(" · ")}
                        {" "}<a href={o.url} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}
                                title={t("pz.d.deschide", { sursa: numeSursa(o.sursa) })}>↗</a>
                        {(o.dubluri?.length ?? 0) > 0 && (
                          <span className="pz-dubluri"> · {t("pz.si_pe")}{" "}
                            {o.dubluri!.map((d, i) => (
                              <a key={`${d.sursa}-${d.id}`} href={d.url} target="_blank" rel="noreferrer noopener"
                                 onClick={(e) => e.stopPropagation()}>{i ? ", " : ""}{numeSursa(d.sursa)} ↗</a>
                            ))}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="sg-mono bc-n"><strong>{bani(o.pret_mp, o.moneda)}</strong><FaraTva o={o} /></td>
                    <td className="sg-mono bc-n">{bani(o.pret, o.moneda)}</td>
                    <td className="sg-mono bc-n">{o.suprafata_mp == null ? "—" : numar(o.suprafata_mp)}</td>
                    <td className="sg-mono bc-n">{o.camere ?? "—"}</td>
                    <td className="sg-mono bc-n">{etaj(o)}</td>
                    <td className="sg-mono"><An o={o} vechime={s.vechime_ani} /></td>
                    <td><Finisaje o={o} /></td>
                    <td><Fatada o={o} /></td>
                    <td className="sg-mono bc-n" title={o.distanta_aprox ? t("pz.aprox_t") : undefined}>
                      {o.distanta_aprox ? "≈ " : ""}{km(o.distanta_km)}</td>
                    <td className="sg-mono bc-n">{numar(o.pondere, { maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {lista.length > limita && (
              <button type="button" className="btn pz-mai" onClick={() => setLimita(limita + PE_PAGINA)}>
                {t("pz.mai_multe", { n: numar(Math.min(PE_PAGINA, lista.length - limita)), tot: numar(lista.length) })}</button>
            )}
          </div>
          {ales != null && (
            <Detaliu projectId={projectId} id={ales} oferta={oferta} vechime={s.vechime_ani} laInchide={() => setAles(null)} />
          )}
        </div>
      )}

    </div>
  );
}
